import type {
  CollabDraftSupportColumn,
  CollabDraftAttributeTestColumn,
  CollabDraftTestingTable,
} from "../../../models/audit/collabPlanTypes";

/**
 * The server's direct-table rule, as the Edit Testing Table dialog can apply
 * it before Save. A refused panel edit is lost without a word once the dialog
 * closes, so the dialog holds the choices the server would refuse instead.
 *
 * The rule: a table that becomes direct holds only Analytics columns, at least
 * one, and a direct table may not gain a Support or Attribute Test column. A
 * saved procedure that a revise session was seeded with as direct (its id is
 * in `seededDirectTableIds`) may hold and gain any column. A saved table (one
 * with an id) may change its procedure type only in a session seeded with the
 * saved procedure types (`procedureBasesSeeded`).
 *
 * Applies only while hybrid testing is not hidden: off the flags the server
 * refuses every newly direct table and leaves an already direct one alone, and
 * the dialog offers no Procedure Type there.
 */

/** The session state the rule judges an edit by, sent beside the plan. */
export interface SeededBases {
  procedureBasesSeeded: boolean;
  seededDirectTableIds: string[];
}

export const DIRECT_HOLDS_SAMPLED_COLUMNS_COPY =
  "A direct table holds only Analytics columns. Remove this table's Support and Attribute Test columns to make it direct.";
export const DIRECT_NEEDS_ANALYTICS_COLUMN_COPY =
  "A direct table holds only Analytics columns, at least one, and this table has none.";
export const DIRECT_REFUSES_SAMPLED_COLUMN_COPY =
  "A direct table holds only Analytics columns. Set the procedure type to Sample to add this column.";
export const SAVED_PROCEDURE_TYPE_KEPT_COPY =
  "This saved table keeps its procedure type in this session. Start a new AI revision from the saved version to change it.";

function seededDirect(
  table: CollabDraftTestingTable | undefined,
  seededBases: SeededBases,
): boolean {
  const id = table?.id;
  return id ? seededBases.seededDirectTableIds.includes(id) : false;
}

/** Why the dialog may not change the table's procedure type at all, or null when it may. */
export function procedureTypeHold(
  initialTable: CollabDraftTestingTable | undefined,
  seededBases: SeededBases,
): string | null {
  return initialTable?.id && !seededBases.procedureBasesSeeded
    ? SAVED_PROCEDURE_TYPE_KEPT_COPY
    : null;
}

/** Why the dialog may not switch the table to Direct, or null when it may. */
export function directChoiceHold(
  initialTable: CollabDraftTestingTable | undefined,
  supportColumns: CollabDraftSupportColumn[],
  attributeTestColumns: CollabDraftAttributeTestColumn[],
  seededBases: SeededBases,
): string | null {
  if (seededDirect(initialTable, seededBases)) return null;
  if (initialTable?.procedureType === "direct") {
    const gainsSampledColumns =
      supportColumns.length > initialTable.supportColumns.length ||
      attributeTestColumns.length > initialTable.attributeTestColumns.length;
    return gainsSampledColumns ? DIRECT_HOLDS_SAMPLED_COLUMNS_COPY : null;
  }
  if (supportColumns.length > 0 || attributeTestColumns.length > 0) {
    return DIRECT_HOLDS_SAMPLED_COLUMNS_COPY;
  }
  if ((initialTable?.analyticsColumns.length ?? 0) === 0) {
    return DIRECT_NEEDS_ANALYTICS_COLUMN_COPY;
  }
  return null;
}

/** Why the dialog may not add a Support or Attribute Test column, or null when it may. */
export function sampledColumnAddHold(
  initialTable: CollabDraftTestingTable | undefined,
  procedureType: CollabDraftTestingTable["procedureType"],
  seededBases: SeededBases,
): string | null {
  if (procedureType !== "direct") return null;
  return seededDirect(initialTable, seededBases)
    ? null
    : DIRECT_REFUSES_SAMPLED_COLUMN_COPY;
}
